/**
 * AcArchive — 작업별 SEO 아카이브 페이지 (/ac-datacenter/w/:slug).
 * 프로그래매틱 SEO: 각 대표 작업마다 고유 URL·메타·콘텐츠(실제 사고사례+위험도+예시 RA/JSA/TBM).
 * 프리렌더(scripts/prerender.mjs)로 초기 HTML에 포함 → 색인 가능.
 */
import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { FiArrowRight, FiArrowLeft, FiSearch } from 'react-icons/fi'
import ForceDarkTheme from '../../components/common/ForceDarkTheme'
import Spinner from '../../components/ui/Spinner'
import { AC_I18N, type AcLang } from './acI18n'
import { acSearch, acAggregate } from './acDataCenterApi'
import { loadExamples } from './acExamples'
import { CaseCard, RiskSnapshot } from './AcDataCenter'
import ExampleShowcase from './ExampleShowcase'

const SIGNUP_PATH = '/signup'

export default function AcArchive() {
  const { i18n } = useTranslation()
  // /en/ 경로는 강제 영어 렌더(영어 SEO 프리렌더 대상). 그 외는 브라우저 언어(i18next).
  const forcedEn = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/')
  const lang: AcLang = forcedEn || i18n.language?.startsWith('en') ? 'en' : 'ko'
  const s = AC_I18N[lang]
  const { slug = '' } = useParams()

  const { data: works } = useQuery({ queryKey: ['acExamples', lang], queryFn: () => loadExamples(lang), staleTime: Infinity })
  const work = useMemo(() => works?.find((w) => w.id === slug) || null, [works, slug])
  const query = work ? work.label.ko : ''

  const searchQ = useQuery({
    queryKey: ['acSearch', query, lang],
    queryFn: () => acSearch(query, lang),
    enabled: !!query,
    staleTime: 5 * 60 * 1000,
  })
  const aggQ = useQuery({
    queryKey: ['acAgg', query, lang],
    queryFn: () => acAggregate(query, lang),
    enabled: !!query,
    staleTime: 5 * 60 * 1000,
  })

  const label = work ? (lang === 'en' ? work.label.en : work.label.ko) : ''
  const koUrl = `https://heinrich.dasancms.co.kr/ac-datacenter/w/${slug}/`
  const enUrl = `https://heinrich.dasancms.co.kr/en/ac-datacenter/w/${slug}/`
  const canonical = lang === 'en' ? enUrl : koUrl
  const title = lang === 'en'
    ? `${label} — real accident cases & risk assessment | Heinrich`
    : `${label} 산업재해 사고사례·위험성평가 예시 | Heinrich`
  const desc = lang === 'en'
    ? `Real ${label} accident cases (AI-cleaned) plus example risk assessment, JSA and TBM. Free, no sign-up.`
    : `${label} 실제 산업재해 사고사례(AI 정제)와 위험성평가·JSA·TBM 예시. 무료·회원가입 없이.`
  const jsonLd = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org', '@type': 'Dataset',
    name: title, description: desc, url: canonical, isAccessibleForFree: true,
    keywords: work?.keywords || [],
  }), [title, desc, canonical, work])

  if (works && !work) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <ForceDarkTheme />
        <p className="text-ink-muted">{lang === 'en' ? 'Not found.' : '페이지를 찾을 수 없습니다.'}</p>
        <Link to="/ac-datacenter" className="mt-4 inline-block text-amber-400">← AC DataCenter</Link>
      </div>
    )
  }

  return (
    <>
      <ForceDarkTheme />
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="ko" href={koUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={koUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-surface-elevated/30">
        <div className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(55% 45% at 50% 0%, rgba(245,158,11,0.09), transparent 70%)' }} />
        <div className="relative mx-auto max-w-5xl px-4 pt-14 pb-8">
          <Link to="/ac-datacenter" className="inline-flex items-center gap-1 text-sm text-ink-faint hover:text-amber-400">
            <FiArrowLeft size={14} /> AC DataCenter
          </Link>
          <h1 className="mt-3 text-2xl font-extrabold text-ink sm:text-4xl">
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">{label}</span>
            {lang === 'en' ? ' — real accidents & risk assessment' : ' 사고사례 · 위험성평가'}
          </h1>
          <p className="mt-3 max-w-2xl text-ink-muted">{desc}</p>
          <Link to={`/ac-datacenter?q=${encodeURIComponent(label)}`} className="mt-5 inline-block">
            <button className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-400">
              <FiSearch size={15} /> {lang === 'en' ? 'Search similar tasks' : '다른 작업도 검색'}
            </button>
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-6xl space-y-8 px-4 py-10">
        {searchQ.isFetching && <div className="flex justify-center py-16"><Spinner size="lg" /></div>}

        {aggQ.data && aggQ.data.by_accident_type && aggQ.data.by_accident_type.length > 0 && (
          <RiskSnapshot agg={aggQ.data} s={s} lang={lang} />
        )}

        {searchQ.data && searchQ.data.cases && searchQ.data.cases.length > 0 && (
          <div>
            <h2 className="mb-3 text-lg font-bold text-ink">{s.casesTitle}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {searchQ.data.cases.map((c) => <CaseCard key={c.id} c={c} s={s} />)}
            </div>
          </div>
        )}

        {/* 예시 RA/JSA/TBM */}
        <ExampleShowcase query={query} lang={lang} />

        {/* 다른 작업 (내부링크) */}
        {works && works.length > 1 && (
          <div className="border-t border-surface-elevated/30 pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-faint">
              {lang === 'en' ? 'Other tasks' : '다른 작업 사고사례'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {works.filter((w) => w.id !== slug).map((w) => (
                <Link
                  key={w.id} to={`/ac-datacenter/w/${w.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-surface-elevated/50 bg-surface-card px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-amber-500/40 hover:text-amber-400"
                >
                  {lang === 'en' ? w.label.en : w.label.ko} <FiArrowRight size={12} />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-7 text-center">
          <h3 className="text-xl font-bold text-ink">{lang === 'en' ? 'Turn these into your own risk assessment' : '이 사고들로 내 위험성평가를 만들어보세요'}</h3>
          <Link to={SIGNUP_PATH} className="mt-4 inline-block">
            <button className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-400">
              {s.gateCta} <FiArrowRight />
            </button>
          </Link>
        </div>
      </section>
    </>
  )
}
